import {
    ESeriesCategory,
    IEvent, IGallery, IGetCloudProductsResponse, IPlan, IPlanSeries, IProductData, IPromotion, IPromotionItem,
    IReolinkDevice, ISeriesItem, ISubDevice, ITotalDevice, TGetDiscountsResult
} from '@reolink-cloud/interface';
import dayjs from 'dayjs';
import { config } from 'CONFIG';
import { getEventList } from '@/api/event';
import { getAiAvailability } from '@/api/ai';
import type VueI18n from 'vue-i18n';
import { TDownloadEntryMap } from './download';
import { formatTime } from '@/common/js/date';
import { unfoldError } from '@/common/js/notice';
import { GetUserDataError } from './error';
import VueRouter from 'vue-router';
import { calcFinalDataStr, groupBy } from '@/common/js/utils';
import * as _ from '@reolink-web/utils';

export const getPlanPromotionText = ( promotions: IPromotionItem[], plan: IPlan ): string | undefined => {

    if ( !promotions?.length ) return undefined;
    const filteredPromotions = promotions.filter( item => item.planIds?.includes( plan.id ));
    if ( !filteredPromotions.length ) return undefined;
    return filteredPromotions.sort(( a, b ) => ( b.createdAt ?? 0 ) - ( a.createdAt ?? 0 )).filter( item => item.text
        && dayjs( item.startTime ?? '0' ) <= dayjs()
        && dayjs( item.endTime ?? dayjs().add( 7, 'days' )) >= dayjs()
    ).map( item => item.text ).join( ', ' );

};

/**
 * 获取优惠活动对应的 usageLimitToItems（优惠生效需要的周期数）。
 * 仅在当前有文案展示的活动中取值，与 getPlanPromotionText 的筛选逻辑保持一致。
 */
export const getPlanPromotionUsageLimitToItems = ( promotions: IPromotionItem[], plan: IPlan ): number | undefined => {

    if ( !promotions?.length ) return undefined;
    const filteredPromotions = promotions.filter( item => item.planIds?.includes( plan.id ));
    if ( !filteredPromotions.length ) return undefined;
    const activePromotion = filteredPromotions
        .sort(( a, b ) => ( b.createdAt ?? 0 ) - ( a.createdAt ?? 0 ))
        .find( item => item.text
            && item.usageLimitToItems != null
            && dayjs( item.startTime ?? '0' ) <= dayjs()
            && dayjs( item.endTime ?? dayjs().add( 7, 'days' )) >= dayjs()
        );
    return activePromotion?.usageLimitToItems;

};

/**
 * 按当前促销 usageLimitToItems 得到应在购物车中设置的数量（受 min/max 约束）。
 * 无适用活动或无法解析时返回 null。
 */
export const getClampedPromotionUsageLimitQty = (
    promotions: IPromotionItem[],
    plan: IPlan,
    minQty: number,
    maxQty: number | undefined
): number | null => {

    const raw = getPlanPromotionUsageLimitToItems( promotions, plan );
    if ( raw == null ) return null;
    const upper = maxQty ?? raw;
    return Math.min( Math.max( raw, minQty ), upper );

};

export const isApp = config.platform === 'app';

export const getDeviceAiSupportStatus = ( device: ISubDevice ): boolean => {

    return config.aiFeatures.some( feature => device[`${feature}Enabled`] );

};

export const getDeviceTitle = ( device: ISubDevice | ITotalDevice | IReolinkDevice | undefined, fallbackToUid?: boolean ): string => {

    if ( !device ) return fallbackToUid ? '' : '';

    if ( 'model' in device && typeof device.model === 'object' && device.model?.title ) {

        return device.title?.trim() || device.model.title;

    }

    if ( device.title && device.title.trim().length > 0 ) {

        return device.title;

    }

    if ( 'modelTitle' in device && device.modelTitle && device.modelTitle.trim().length > 0 ) {

        return device.modelTitle;

    }

    return fallbackToUid ? device.uid : ( typeof device.model === 'string' ? device.model : '' );

};

export const getEventInfo = async ( id: string ): Promise<IEvent> => {

    const res = await getEventList({ idList: [id] });

    return res.items[0];

};

export const getEventVideoUrls = ( event: IEvent ): string[] => {

    const videoFiles = event.items.filter( item => item.type === 'video' ).reduce<IGallery['files']>(( acc, item ) => {

        acc = acc.concat( item.files.filter( file => file.type === 'video' && file.url ));

        return acc;

    }, [] );
    return videoFiles.map( file => file.url );

};

export const getCountryWithoutOther = ( country: string ): string => {

    return country === 'other' ? config.fallbackCountry : country;

};

export const isEuropeCountry = ( country?: string ): boolean => {

    return config.countryData.countries?.find(
        item => item.countryCode.toUpperCase() === country?.toUpperCase()
    )?.continent?.toLowerCase() === 'europe';

};

export const shouldCheckAgreementByDefault = ( country?: string ): boolean => {

    return Boolean( config.countryData.countries?.length ) && !isEuropeCountry( country );

};

export const getShowAiInfo = async ( country: string, features?: string ): Promise<boolean> => {

    const aiAvailability = await getAiAvailability({
        country: getCountryWithoutOther( country ),
        features: features ?? config.aiFeatures.join( ',' )
    });
    return aiAvailability.smartAiLaunchedCountries.includes( country );

};

export const getDiscounts = ( promotionTextMap: Record<string, string>, promotion: IPromotion[] = [] ): TGetDiscountsResult => {

    if ( promotion.length === 0 ) {

        return;

    }

    const getPromotionTitle = ( key: string ): string => {

        if ( key in promotionTextMap ) {

            return promotionTextMap[key];

        } else {

            return promotionTextMap['default'];

        }

    };

    return promotion.map( promotionItem => ({
        title: getPromotionTitle( promotionItem.key ),
        details: promotionItem.discount
    }));

};

export const getI18nPromotionTextMap = ( i18n: VueI18n, locale?: string ): Record<string, string> => {

    return {
        pd202507: i18n.t( 'cart.pd_discount', locale ?? i18n.locale ).toString(),
        regress: i18n.t( 'cart.regress_discount', locale ?? i18n.locale ).toString(),
        cloud_coupon: i18n.t( 'cart.coupon.title', locale ?? i18n.locale ).toString(),
        first_charge_promotion: i18n.t( 'cart.first_charge_promotion', locale ?? i18n.locale ).toString(),
        cloudExposureOne: i18n.t( 'cart.discount', locale ?? i18n.locale ).toString(),
        cloudExposureSeven: i18n.t( 'cart.discount', locale ?? i18n.locale ).toString(),
        default: i18n.t( 'cart.discount_title', locale ?? i18n.locale ).toString(),
    };

};

export const isOtherRegion = localStorage.getItem( 'country' ) === 'other';
export const getFileNameFromTime = ( time: number ): string => formatTime( time ).replace( /\/|:/g, '-' );

export const generateDownloadMapFromEvents = ( events: IEvent[] ): TDownloadEntryMap => {

    const downloadMap: TDownloadEntryMap = {};
    for ( const event of events ) {

        for ( const item of event.items ) {

            for ( const file of item.files ) {

                if ( file.type === 'video' && file.url ) {

                    const id = JSON.stringify( [
                        event.id,
                        item.id,
                        file.resolution,
                        file.uploadedAt,
                        file.size
                    ] );
                    downloadMap[id] = {
                        name: getFileNameFromTime( file.uploadedAt ),
                        url: file.url
                    };

                }

            }

        }

    }
    return downloadMap;

};

export const getDownloadText = ( i18n: VueI18n, events: IEvent[] ): string => {

    const videoCount = Object.keys( generateDownloadMapFromEvents( events )).length;

    return videoCount <= 1 ? i18n.tc( 'video.download' ) : i18n.tc( 'video.download_zip' );

};

export const getCountryPromotionText = ( promotions: IPromotionItem[], planList: IPlan[] ): string | undefined => {

    if ( !promotions?.length ) return undefined;

    const DAY_NOW = dayjs();
    const DAY_7_DAYS_LATER = dayjs().add( 7, 'days' );
    return promotions.sort(( a, b ) => ( b.createdAt ?? 0 ) - ( a.createdAt ?? 0 )).find( item => item.countryText
        && dayjs( item.startTime ?? '0' ) <= DAY_NOW
        && dayjs( item.endTime ?? DAY_7_DAYS_LATER ) >= DAY_NOW
        && planList.some( plan => item.planIds?.includes( plan.id ))
    )?.countryText;

};

export const handleGetUserDataError = ( error: unknown, router: VueRouter ): void => {

    if ( error instanceof GetUserDataError ) {

        unfoldError( 'errors.subscription_not_found', 9999999, true, async () => {

            await router?.push( error.isSimError ? {
                name: 'sim-plan-list',
                replace: true
            } : {
                name: 'subscribe-cloud',
                replace: true
            });

        });

    } else {

        unfoldError( 'errors.server_error' );

    }

};

export const isValidIccid = ( iccid: unknown ): boolean => {

    if ( typeof iccid === 'string' ) return iccid.length === 20;
    return false;

};

export const isUnlimitedDataSize = ( plan?: IPlan ): boolean => {

    return plan?.style['cloud_storage.unlimited']?.includes( 'data_size' ) ?? false;

};

export const getPlanDataSizeStr = ( plan: IPlan|undefined, i18n: VueI18n ): string => {

    const rawSize = plan?.style['cloud_storage.data_size'] ?? 0;
    return i18n.tc( 'dataPackageSize', rawSize, {
        count: isUnlimitedDataSize( plan ) ? i18n.tc( 'plan.unlimited' ) : calcFinalDataStr( rawSize, true )
    });

};

export const queryStorageApi = new _.Storage.WebStorage<
    {
        query_support: string;
        query_source: string;
    }
>({
    type: 'sessionStorage',
    keys: ['query_support', 'query_source']
});

export const doorbellProgressStorage = new _.Storage.WebStorage<
    {
        doorbell_progress_uid: string;
    }
>({
    type: 'sessionStorage',
    keys: ['doorbell_progress_uid']
});

export const getQuerySource = (): string | undefined => {

    return queryStorageApi.getItem( 'query_source', {
        shouldXssFilter: false
    }) ?? undefined;

};

export const pendingBindDevicesStorage = new _.Storage.WebStorage<{
    pendingBindDevices: string[];
}>({
    type: 'sessionStorage',
    keys: ['pendingBindDevices']
});

export const animatedPlanFilter = ( plan: IPlan ): boolean => {

    if ( queryStorageApi.getItem( 'query_support' )?.includes( 'animated' )) {

        return plan.style['cloud_storage.rich_notification'] === 'static_and_dynamic_image';

    }
    return true;

};

export const isFreePlan = ( plan: IPlan ): boolean => {

    return parseFloat( plan.pricing.salePrice ) === 0;

};

export const isSupportRichNotification = ( plan: IPlan ): boolean => {

    return plan.style['cloud_storage.allow_rich_notification'] ?? ![undefined, 'disabled'].includes( plan.style['cloud_storage.rich_notification'] );

};

export const isUnlimitedStoragePlan = ( plan?: IPlan ): boolean => {

    const unlimitedArr = plan?.style['cloud_storage.unlimited'];
    return Array.isArray( unlimitedArr ) && unlimitedArr.includes( 'space_capacity' );

};

interface IPlanFilter { country?: string; category?: ESeriesCategory; legacyTag?: string; keepBasic?: boolean; }
export const getPlanSeriesListByFilter = ( cloudProductLists: IProductData['cloudProductLists'], filter: IPlanFilter ): ISeriesItem[] => {

    const seriesList = Object.values( cloudProductLists.planSeries ).filter( series => {

        if ( filter.country && !series.countryGroups.some( countryGroup =>
            cloudProductLists.countryGroups[countryGroup]?.countries?.some( country =>
                country.key === filter.country )))
            return false;
        if ( filter.category && series.category !== filter.category ) return false;
        const legacyFilter = filter.legacyTag ? series.legacyTag === filter.legacyTag : !series.legacyTag;
        const basicFilter = ( filter.keepBasic ?? false ) || !series.key.includes( 'basic' );

        return legacyFilter && basicFilter;

    });
    return seriesList.map( series => ({
        seriesName: series.i18n.name,
        products: Object.values( cloudProductLists.plans ).filter( plan => plan.seriesKey === series.key ).filter( plan => plan.status === 'enabled' ),
        sortOrder: series.style['series.sort'],
        deviceGroup: series.deviceGroup
    })).filter( series => series.products.length > 0 );

};

export const getDeviceGroupWithPlans = (
    cloudProductLists: IProductData['cloudProductLists'], filter: IPlanFilter
) => {

    const planSeriesList = getPlanSeriesListByFilter( cloudProductLists, filter );
    const planSeriesGroupByDeviceGroup = groupBy( planSeriesList, series => series.deviceGroup );
    return Object.entries( planSeriesGroupByDeviceGroup ).map(( [deviceGroup, seriesList]: [string, ISeriesItem[]] ) => {

        const deviceGroupInfo = cloudProductLists.deviceModelGroups[deviceGroup];
        return {
            key: deviceGroup,
            planList: seriesList,
            title: deviceGroupInfo?.i18n.name,
            des: deviceGroupInfo?.i18n.description,
            models: deviceGroupInfo?.models,
            modelIds: deviceGroupInfo?.modelIds,
            sort: deviceGroupInfo.metadata.sort
        };

    });

};
